<script>
  import StationMap from "./components/station/StationMap.svelte";
  import StationSidebar from "./components/station/StationSidebar.svelte";
  import { onMount, onDestroy } from "svelte";
  import { error, viewMode, stationDataActions, initFromUrl, displayStations, selectedStations, mapInstance, focusedStation } from "./services/station-service.ts";
  import { SearchState } from "./services/search-service.js";

  let returnHandled = false;
  let returnStationId = null;

  SearchState.subscribe((search) => {
    returnStationId = search.params.get("stations");
  });

  $: if (returnStationId && !returnHandled && $selectedStations.length > 0 && $displayStations.length > 0 && $mapInstance) {
    const station = $displayStations.find(s => s.id === $selectedStations[0]);
    if (station) {
      returnHandled = true;
      focusedStation.set(station);
    }
  }

  const DEFAULT_MAP_WIDTH = 70;
  const MIN_MAP_WIDTH = 40;
  const MAX_MAP_WIDTH = 85;
  const HANDLE_WIDTH = 0.5;

  let mapWidth = DEFAULT_MAP_WIDTH;
  let isDragging = false;
  let initialX;
  let initialMapWidth;
  let containerRef = null;

  $: mapWidth = Math.max(MIN_MAP_WIDTH, Math.min(MAX_MAP_WIDTH, mapWidth));

  function handleDragStart(event) {
    isDragging = true;
    initialX = event.clientX;
    initialMapWidth = mapWidth;
    containerRef = document.querySelector(".map-container-flex");

    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", handleDragEnd);

    document.body.style.userSelect = "none";
  }

  function handleDragMove(event) {
    if (!isDragging || !containerRef) return;

    const containerWidth = containerRef.offsetWidth;
    const deltaX = event.clientX - initialX;
    const deltaPercentage = (deltaX / containerWidth) * 100;
    mapWidth = Math.max(MIN_MAP_WIDTH, Math.min(MAX_MAP_WIDTH, initialMapWidth - deltaPercentage));
  }

  function handleDragEnd() {
    if (!isDragging) return;

    isDragging = false;

    window.removeEventListener("mousemove", handleDragMove);
    window.removeEventListener("mouseup", handleDragEnd);

    document.body.style.userSelect = "";
  }

  function cleanup() {
    if (isDragging) {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      document.body.style.userSelect = "";
      isDragging = false;
    }

    window.removeEventListener("popstate", initFromUrl);
  }

  onMount(() => {
    initFromUrl();
    window.addEventListener("popstate", initFromUrl);
  });

  onDestroy(cleanup);
</script>

<div class="container-fluid station-explorer">
  <div class="row">
    <div class="col-md-12 map-container-flex d-flex px-0">
      <StationSidebar width={100 - mapWidth - HANDLE_WIDTH} />

      <div
        class="resize-handle"
        class:active={isDragging}
        on:mousedown={handleDragStart}
        role="separator"
        aria-label="Resize panels"
        tabindex="0"
      >
        <div class="handle-bar"></div>
      </div>

      <StationMap width={mapWidth} />
    </div>
  </div>

  {#if $error}
    <div class="error-overlay">
      <div class="alert alert-danger">
        <h4>Error Loading Stations</h4>
        <p>{$error}</p>
        <button
          class="btn btn-primary"
          on:click={() => {
            if ($viewMode === "viewport") {
              window.location.reload();
            } else {
              stationDataActions.loadStations();
            }
          }}
        >
          Try Again
        </button>
      </div>
    </div>
  {/if}
</div>

<style>
  .station-explorer {
    --primary-color: #007ec6;
  }

  .map-container-flex {
    display: flex;
    height: 800px;
    border-bottom: 1px solid #dee2e6;
  }

  .resize-handle {
    width: 0.5%;
    height: 100%;
    background-color: #e9ecef;
    cursor: col-resize;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    transition: background-color 0.2s ease;
  }

  .resize-handle:hover {
    background-color: #adb5bd;
  }

  .resize-handle.active {
    background-color: var(--primary-color);
  }

  .resize-handle:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  .handle-bar {
    height: 50px;
    width: 4px;
    background-color: #adb5bd;
    border-radius: 2px;
    transition: background-color 0.2s ease;
  }

  .resize-handle:hover .handle-bar,
  .resize-handle.active .handle-bar {
    background-color: white;
  }

  .error-overlay {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
    max-width: min(400px, calc(100vw - 2rem));
  }

  @media (max-width: 767px) {
    .map-container-flex {
      flex-direction: column;
      height: auto;
    }
    .resize-handle {
      display: none;
    }
  }
</style>